import { merge } from 'lodash';
import { BrowserMode } from '@/store/modules/entity_browser/config';
import LANG from '@/lang/eng';

const withOrder = (columns) => Object.entries(columns)
  .reduce((acc, [key, col], index) => ({
    ...acc,
    [key]: {
      ...col,
      order: index,
      active: false,
    },
  }), {});

export const Columns = withOrder({
  expand: {
    label: '',
    name: 'expand',
    width: 40,
    isAlwaysOn: true,
  },
  select: {
    label: LANG.select,
    name: 'select',
    width: 45,
    isAlwaysOn: true,
  },
  preview_url: {
    label: LANG.icon,
    name: 'preview_url',
    width: 70,
  },
  title: {
    label: LANG.title,
    name: 'title',
    sortable: true,
    maxWidth: 400,
  },
  actions: {
    label: LANG.actions,
    name: 'actions',
    maxWidth: 180,
    isAlwaysOn: true,
  },
  directory: {
    label: LANG.directory,
    name: 'directory',
    sortable: true,
    maxWidth: 300,
  },
  url: {
    label: LANG.uri,
    name: 'url',
    sortable: true,
    maxWidth: 300,
  },
  filename: {
    label: LANG.filename,
    name: 'filename',
    sortable: true,
    maxWidth: 400,
  },
  last_editor: {
    label: LANG.lastEditor,
    name: 'last_editor',
    width: 150,
  },
  finished_at: {
    label: LANG.lastFinished,
    name: 'finished_at',
    sortable: true,
    width: 200,
  },
  published_at: {
    label: LANG.publishedAt,
    name: 'published_at',
    sortable: true,
    width: 200,
  },
  category: {
    label: LANG.category,
    name: 'category',
    sortable: true,
    width: 200,
  },
  publish_date: {
    label: LANG.publishDate,
    name: 'publish_date',
    sortable: true,
    width: 200,
  },
  expiry_date: {
    label: LANG.expiryDate,
    name: 'expiry_date',
    sortable: true,
    width: 200,
  },
  resubmit_date: {
    label: LANG.resubmitDate,
    name: 'resubmit_date',
    width: 200,
  },
  created_by: {
    label: LANG.createdBy,
    name: 'created_by',
    width: 200,
  },
  created_at: {
    label: LANG.firstCreatedDate,
    name: 'created_at',
    sortable: true,
    width: 200,
  },
  live_url: {
    label: LANG.liveURI,
    name: 'live_url',
    width: 200,
  },
  is_up_to_date: {
    label: LANG.published,
    name: 'is_up_to_date',
    maxWidth: 80,
  },
  references: {
    label: LANG.references,
    name: 'references',
    width: 160,
  },
  template: {
    label: LANG.template,
    name: 'template',
  },
});

const docColumns = {
  expand: { active: true },
  select: { active: true },
  preview_url: { active: true },
  title: { active: true },
  actions: { active: true },
  url: { active: true },
  last_editor: { active: true },
  finished_at: { active: true },
  published_at: { active: true },
  is_up_to_date: { active: true },
  references: { active: true },
};

const mamColumns = {
  // expand: { active: true },
  select: { active: true },
  preview_url: { active: true },
  title: { active: true },
  actions: { active: true },
  url: { active: true },
  last_editor: { active: true },
  finished_at: { active: true },
  references: { active: true },
  dimensions: {
    label: LANG.dimensions,
    name: 'dimensions',
  },
  filesize: {
    label: LANG.filesize,
    name: 'filesize',
    maxWidth: 80,
  },
};

/**
 * @param {*} hiddenFieldsConfig The value of the `MAM_HIDDEN_FIELDS` system conf variable
 * @returns a predicate to check if a field is hidden
 */
export function isHiddenPredicate(hiddenFieldsConfig = '') {
  // Supported config:
  // https://portal.pirobase-imperia.com/pb/imperia-cms-dokumentation/admin-de-10/admin.conf/#mam_hidden_fields-variable
  // https://confluence.pirobase.de/pages/viewpage.action?pageId=155320657
  const legacyIds = {
    __mam_select: 'select',
    __mam_icon: 'preview_url',
    __mam_title: 'title',
    __mam_filename: 'filename',
    __mam_filesize: 'filesize',
    __mam_references: 'references',
    __mam_createdby: 'created_by',
    __pub_createdby: 'created_by',
    __mam_editedby: 'last_editor',
    __pub_editedby: 'last_editor',
    __mam_createdat: 'created_at',
    __mam_dimensions: 'dimensions',
    __mam_directory: 'directory',
    __mam_template: 'template',
    __wf_category: 'category',
    __pub_uri: 'url',
    __pub_live_uri: 'live_url',
    __pub_created_date: 'finished_at',
    __pub_published_at: 'published_at',
    __pub_publish_date: 'publish_date',
    __pub_expiry_date: 'expiry_date',
    __pub_resubmit_date: 'resubmit_date',

    // actions:
    __mam_reimport: 'edit',
    __pub_import: 'edit',
    __mam_actions: 'customize',
    __mam_delete: 'delete',
    __mam_archive: 'differences',
    __mam_details: 'info',
    __mam_focusarea: 'focuspoint',
    __pub_history: 'history',
    __mam_info: 'metaviewer',
    __doc_preview: 'preview',
    __pub_publish: 'publish',
    __pub_quickedit: 'quickedit',
    __pub_reparse: 'reparse',
    __mam_variants: 'variants',
    __pub_unpublish: 'unpublish',
    __mam_zoom: 'zoom',
  };

  const fields = hiddenFieldsConfig
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
    .map((f) => legacyIds[f] || f); // remap legacy identifiers

  const match = new RegExp(`^(${fields.join('|')})$`);

  return function isHidden(field) {
    return match.test(field);
  };
}

export default {
  [BrowserMode.DOC]: merge({}, Columns, docColumns),
  [BrowserMode.MAM]: merge({}, Columns, mamColumns),
};
